import { Popover, Position } from "@blueprintjs/core"
import { actions } from "@src/Actions/Reducers/GlobalModalsReducer"
import Tooltip from "@src/Components/Tooltip"
import { useLocale } from "@src/Hooks/useLocale"
import { tState } from "@src/Model/Model"
import { colors } from "@src/theme"
import * as T from "@src/types"
import { ViewTypes } from "@src/types"
import { Section } from "@zeguro/component-library"
import { Locale as ILocale } from "@zeguro/schema-validator/dist/types/coco/core/combined"
import { useDispatch, useSelector } from "react-redux"
import { useHistory, useRouteMatch } from "react-router-dom"

import C from "../../Controller"
import { usePortalSize } from "../Common/usePortalSize"
import { FaIcon } from "../FaIcon"
import { LocaleSelect } from "../LocaleSelect"
import {
  AUTH_HEADER_GUTTER,
  CompanyName,
  Logo,
  LogoImg,
  MenuIcon,
  Navbar,
  SignOutButton,
  UserIcon,
  UserInfo,
  UserInfoContent,
  UserName,
  UserNameContainer,
  ZeguroSessionBanner,
} from "./styles"

interface IHeader {
  height: number
  offsetTop: number
  uiRules?: T.tChannelUiRules
  hideBorder?: boolean
  authenticated: boolean
  onBoarding: boolean
  isFirstTimeLogin: boolean
  isIncorrectURL: boolean
  onOpenMenu: () => void
  rightAction?: React.ReactNode
}

interface IRoutesParams {
  subscriptionId?: string
}

const Header = ({
  height,
  offsetTop,
  uiRules,
  hideBorder = true,
  authenticated,
  onBoarding,
  isFirstTimeLogin,
  isIncorrectURL,
  onOpenMenu,
  rightAction,
}: IHeader) => {
  const { isDesktop, clientWidth } = usePortalSize()
  const history = useHistory()
  const dispatch = useDispatch()

  const { currentLocale } = useLocale()
  const isZeguroServiceSession = useSelector(
    (state: tState) => state.portal.isZeguroServiceSession,
  )
  const userProfile = useSelector((state: tState) => state.portal.userProfile)
  const companyName = useSelector(
    (state: tState) => state.portal.companyProfile?.companyName,
  )
  const brandLogo = useSelector((state: tState) => state.brand.brandLogo)

  const email = userProfile?.email || ""
  const isLongEmail = email.length > 25
  const emailBody = email.split("@")[0]
  const emailTail = `@${email.split("@")[1]}`
  const truncatedEmailBody =
    emailBody.length > 25 ? `${emailBody.slice(0, 25)}...` : emailBody
  const username = [userProfile?.firstName, userProfile?.lastName]
    .filter((name) => name)
    .join(" ")
  const firstName = userProfile?.firstName || ""
  const lastName = userProfile?.lastName || ""

  const truncatedFirstName =
    firstName.length > 15 ? `${firstName.slice(0, 15)}...` : firstName
  const truncatedLastName =
    lastName.length > 15 ? `${lastName.slice(0, 15)}...` : lastName

  const displayUsername = [truncatedFirstName, truncatedLastName]
    .filter((name) => name)
    .join(" ")

  const usernameLine1 =
    displayUsername || (isLongEmail ? truncatedEmailBody : email)
  const usernameLine2 = !username && isLongEmail && emailTail

  const shouldShowUserNameTooltip = displayUsername !== username

  const isInAuthFlow =
    onBoarding || !authenticated || isFirstTimeLogin || isIncorrectURL

  const match = useRouteMatch<IRoutesParams>([
    ViewTypes.signinWithSubscriptionId,
    "/signup/:subscriptionId",
    "/resetPassword/:linkId/:subscriptionId",
    "/forgotPassword/:subscriptionId",
  ])

  const handleLogoClick = () => {
    if (authenticated) {
      history.push("/")
    } else {
      history.push(
        match?.params?.subscriptionId
          ? `/signin/${match.params.subscriptionId}`
          : ViewTypes.signin,
      )
    }
  }

  const handleLocaleChange = (newLocale: ILocale) => {
    if (currentLocale !== newLocale) {
      dispatch(actions.openLanguageModal(newLocale))
    }
  }

  if (
    isDesktop &&
    authenticated &&
    !onBoarding &&
    !isFirstTimeLogin &&
    !isIncorrectURL
  ) {
    return null
  }

  return (
    <>
      {isInAuthFlow ? (
        <>
          <Navbar
            height="70"
            padding={`0 ${AUTH_HEADER_GUTTER}`}
            hideBorder
            $authFlow
          >
            <Logo onClick={handleLogoClick}>
              {uiRules?.marketingLogoSecondary ? (
                <LogoImg isCustom src={uiRules?.marketingLogoSecondary} />
              ) : (
                <LogoImg src={brandLogo} />
              )}
            </Logo>
            <Section
              flexLayout="flex-end"
              flex="1"
              margin={
                isDesktop &&
                (onBoarding ||
                  (authenticated && (isFirstTimeLogin || isIncorrectURL)))
                  ? "0 210px 0 0"
                  : "0"
              }
            >
              <LocaleSelect
                value={currentLocale}
                onChange={handleLocaleChange}
                margin="0"
                menuPlacement="bottom"
              />
            </Section>
          </Navbar>
          {(onBoarding ||
            (authenticated && (isFirstTimeLogin || isIncorrectURL))) && (
            <UserInfoContent>
              {isDesktop && (
                <Popover
                  key={clientWidth}
                  minimal
                  content={
                    <SignOutButton
                      className="bp5-menu-item bp5-popover-dismiss"
                      onClick={() => C.Portal.logout()}
                    >
                      {!isDesktop && <UserName>{username || email}</UserName>}
                      <a>
                        <FaIcon
                          className="far fa-sign-out"
                          color={colors.gray40}
                        />
                        &nbsp; Sign Out
                      </a>
                    </SignOutButton>
                  }
                  position={Position.BOTTOM}
                >
                  <UserInfo>
                    {isDesktop && <UserIcon className="fas fa-user-circle" />}
                    {!isDesktop && <FaIcon className="fas fa-sort-down" />}
                    {isDesktop && (
                      <>
                        <UserNameContainer
                          data-tooltip-id={
                            !username
                              ? "full-email-tooltip"
                              : "full-username-tooltip"
                          }
                        >
                          <UserName>{usernameLine1}</UserName>&nbsp;
                          {usernameLine2 && (
                            <>
                              <br />
                              <UserName>{usernameLine2}</UserName>&nbsp;
                            </>
                          )}
                          <FaIcon
                            className="fas fa-sort-down"
                            verticalAlign="top"
                            margin="1px 0 0"
                          />
                        </UserNameContainer>
                        {!username && emailBody !== truncatedEmailBody && (
                          <Tooltip
                            id="full-email-tooltip"
                            content={<p>{email}</p>}
                          />
                        )}
                        {shouldShowUserNameTooltip && (
                          <Tooltip
                            id="full-username-tooltip"
                            content={<p>{username}</p>}
                          />
                        )}
                      </>
                    )}
                    {isDesktop && companyName && (
                      <CompanyName>{companyName}</CompanyName>
                    )}
                  </UserInfo>
                </Popover>
              )}
            </UserInfoContent>
          )}
        </>
      ) : (
        <Navbar height={height} offsetTop={offsetTop} hideBorder={hideBorder}>
          {isZeguroServiceSession ? (
            <ZeguroSessionBanner>
              Attention! Cyber Safety service session is in progress!
            </ZeguroSessionBanner>
          ) : null}
          <MenuIcon className="fas fa-bars" onClick={onOpenMenu} />
          {rightAction}
        </Navbar>
      )}
    </>
  )
}

export default Header
